import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  FiCheckCircle,
  FiCheck,
  FiFileText,
  FiCreditCard,
  FiHome,
  FiBarChart2,
  FiShield,
  FiGrid,
  FiSettings,
  FiUsers,
  FiTrendingUp,
  FiTrendingDown,
  FiLayers,
  FiUserCheck,
  FiLock,
  FiKey,
  FiDatabase,
  FiSmartphone,
  FiChevronDown,
  FiArrowRight,
  FiDownload,
} from "react-icons/fi";
import CtaBanner from "../components/CtaBanner";
import SectionHeading from "../components/SectionHeading";
import Reveal from "../components/Reveal";
import StarRating from "../components/StarRating";
import api, { FALLBACK_PLANS, formatCents } from "../services/api";

const HERO_CHECKS = ["Billed yearly", "Setup in minutes", "Cancel any time"];

const FEATURES = [
  {
    icon: FiFileText,
    title: "Invoicing",
    copy: "Create professional invoices in seconds and get paid faster with online payments.",
  },
  {
    icon: FiCreditCard,
    title: "Expenses",
    copy: "Track and categorize expenses with ease. Snap receipts and stay organized.",
  },
  {
    icon: FiHome,
    title: "Banking",
    copy: "Connect your bank accounts and reconcile transactions automatically.",
  },
  {
    icon: FiBarChart2,
    title: "Reports",
    copy: "Generate real-time financial reports: P&L, balance sheet, cash flow, and more.",
  },
  {
    icon: FiGrid,
    title: "Multi-Company",
    copy: "Manage up to 4 companies in one account and switch between them instantly.",
  },
  {
    icon: FiShield,
    title: "Secure & Reliable",
    copy: "Bank-level security and encryption to keep your financial data protected.",
  },
];

const STEPS = [
  {
    icon: FiLayers,
    title: "Choose your plan",
    copy: "Pick the plan that fits your business today — Starter, Professional, or Business. Each one is billed once a year.",
  },
  {
    icon: FiUserCheck,
    title: "Get approved & onboarded",
    copy: "Create your account, and our team verifies and activates it. Import your chart of accounts or start from a proven template.",
  },
  {
    icon: FiTrendingUp,
    title: "Run your books with confidence",
    copy: "Invoice, reconcile, and report from one clean workspace — with balanced books guaranteed by real double-entry accounting.",
  },
];

const SECURITY_POINTS = [
  {
    icon: FiLock,
    title: "Bank-level encryption",
    copy: "TLS in transit and 256-bit AES encryption at rest for every record.",
  },
  {
    icon: FiUsers,
    title: "Role-based permissions",
    copy: "Grant each teammate exactly the access they need — nothing more.",
  },
  {
    icon: FiFileText,
    title: "Append-only audit trail",
    copy: "Every change is logged and immutable, so your history is always defensible.",
  },
  {
    icon: FiDatabase,
    title: "Automated backups",
    copy: "Continuous, geo-redundant backups keep your data safe and restorable.",
  },
  {
    icon: FiSmartphone,
    title: "Two-factor authentication",
    copy: "Protect every login with a second factor across your whole team.",
  },
];

const SECURITY_BADGES = [
  "SOC 2 Aligned",
  "256-bit AES",
  "GDPR Ready",
  "Daily Backups",
];

/**
 * What the software sets out to do, in her words.
 *
 * Every supporting line names something already built, so nothing here has to
 * be walked back later: no customer counts, no ratings, no forecasting.
 */
const PROMISES = [
  {
    icon: FiTrendingUp,
    title: "Less bookkeeping. More business.",
    body: "Built for small businesses — spend the day running the company, not writing up the books.",
  },
  {
    icon: FiCheckCircle,
    title: "Accounting made clear from day one.",
    body: "A full chart of accounts, customers and vendors ready to go the first time you sign in.",
  },
  {
    icon: FiBarChart2,
    title: "Know your numbers. Grow with confidence.",
    body: "A live dashboard and reports that build themselves from the entries you have already made.",
  },
  {
    icon: FiLayers,
    title: "Smarter books. Simpler business.",
    body: "Categorising, invoicing, payroll and bank reconciliation, handled in one place.",
  },
  {
    icon: FiArrowRight,
    title: "Financial clarity for every next move.",
    body: "See exactly where the business stands before you decide what comes next.",
  },
];

const FAQS = [
  {
    q: "Is this a subscription?",
    a: "Yes — each plan is billed once a year, and renews automatically unless you cancel. You can cancel at any time and keep your access until the year you have paid for runs out.",
  },
  {
    q: "Can I switch plans later?",
    a: "Absolutely. You can upgrade or downgrade at any time from your account settings, and we'll prorate the difference automatically on your next bill.",
  },
  {
    q: "Is my financial data secure?",
    a: "Yes. Your data is protected with bank-level 256-bit encryption, role-based permissions, two-factor authentication, and continuous automated backups — plus an append-only audit trail of every change.",
  },
  {
    q: "Can I manage more than one company?",
    a: "Professional and Business plans support up to 4 companies under a single account, each with its own books, users, and reports — switch between them instantly.",
  },
];

const SIDEBAR_ITEMS = [
  { icon: FiGrid, label: "Dashboard", active: true },
  { icon: FiFileText, label: "Invoices" },
  { icon: FiCreditCard, label: "Expenses" },
  { icon: FiHome, label: "Banking" },
  { icon: FiBarChart2, label: "Reports" },
  { icon: FiUsers, label: "Payroll" },
  { icon: FiSettings, label: "Settings" },
];

/**
 * The newest approved reviews, on the home page.
 *
 * Renders nothing at all while there are none. An empty "what customers say"
 * band reads worse than no band, and inventing reviews to fill it is what the
 * client asked to have taken off this site in the first place.
 */
function HomeReviews() {
  const [reviews, setReviews] = useState([]);
  const [average, setAverage] = useState(null);

  useEffect(() => {
    let mounted = true;
    api.listReviews(3).then((res) => {
      if (!mounted || !res.ok) return;
      setReviews(res.reviews || []);
      setAverage(res.averageRating);
    });
    return () => {
      mounted = false;
    };
  }, []);

  if (reviews.length === 0) return null;

  return (
    <section className="border-y border-line bg-surface">
      <div className="section-container py-20 lg:py-24">
        <SectionHeading
          overline="Reviews"
          title="What Our Customers Say"
          subtitle={
            average !== null
              ? `Rated ${average.toFixed(1)} out of 5 by the people who use it every day.`
              : "From the people who use it every day."
          }
        />
        <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-3">
          {reviews.map((review, i) => (
            <Reveal key={review.id} delay={i * 100}>
              <article className="card flex h-full flex-col p-6">
                <StarRating value={review.rating} />
                {review.title && (
                  <h3 className="mt-3 font-semibold text-navy">{review.title}</h3>
                )}
                <p className="mt-2 flex-1 text-sm leading-relaxed text-slate-600">
                  {review.body}
                </p>
                <footer className="mt-4 border-t border-line pt-4">
                  <p className="text-sm font-semibold text-navy">{review.name}</p>
                  {review.role && <p className="text-xs text-slate-500">{review.role}</p>}
                </footer>
              </article>
            </Reveal>
          ))}
        </div>
        <div className="mt-10 text-center">
          <Link
            to="/reviews"
            className="inline-flex items-center gap-2 text-sm font-semibold text-primary hover:underline"
          >
            Read all reviews, or write one <FiArrowRight size={16} />
          </Link>
        </div>
      </div>
    </section>
  );
}

function DashboardPreview() {
  return (
    <div className="card overflow-hidden shadow-lifted">
      {/* Browser-style top bar */}
      <div className="flex items-center gap-2 border-b border-line bg-surface px-4 py-2.5">
        <span className="h-2.5 w-2.5 rounded-full bg-red-400" />
        <span className="h-2.5 w-2.5 rounded-full bg-amber-400" />
        <span className="h-2.5 w-2.5 rounded-full bg-emerald-400" />
        <span className="ml-3 text-xs font-semibold text-navy">Dashboard</span>
      </div>

      <div className="flex">
        {/* Mini sidebar */}
        <div className="hidden w-32 shrink-0 border-r border-line bg-surface/60 p-2 sm:block">
          {SIDEBAR_ITEMS.map(({ icon: Icon, label, active }) => (
            <div
              key={label}
              className={`mb-1 flex items-center gap-2 rounded-md px-2 py-1.5 text-[11px] font-medium ${
                active ? "bg-primary/10 text-primary" : "text-slate-500"
              }`}
            >
              <Icon size={12} />
              {label}
            </div>
          ))}
        </div>

        {/* Main panel */}
        <div className="flex-1 p-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-lg border border-line p-3">
              <p className="text-[10px] font-medium text-slate-500">
                Total Revenue
              </p>
              <p className="mt-1 text-lg font-bold text-navy">$125,000</p>
              <p className="mt-1 inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-600">
                <FiTrendingUp size={10} /> +12.5% from last month
              </p>
            </div>
            <div className="rounded-lg border border-line p-3">
              <p className="text-[10px] font-medium text-slate-500">
                Net Profit
              </p>
              <p className="mt-1 text-lg font-bold text-navy">$46,750</p>
              <p className="mt-1 inline-flex items-center gap-1 text-[10px] font-semibold text-red-500">
                <FiTrendingDown size={10} /> -1.7% from last month
              </p>
            </div>
          </div>

          <div className="mt-3 rounded-lg border border-line p-3">
            <div className="flex items-center justify-between">
              <p className="text-[11px] font-semibold text-navy">
                Cash Flow Overview
              </p>
              <div className="flex items-center gap-3 text-[9px] text-slate-500">
                <span className="inline-flex items-center gap-1">
                  <span className="h-1.5 w-1.5 rounded-full bg-primary" />
                  Income
                </span>
                <span className="inline-flex items-center gap-1">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
                  Expenses
                </span>
              </div>
            </div>
            <svg
              viewBox="0 0 300 110"
              className="mt-2 h-28 w-full"
              preserveAspectRatio="none"
              aria-hidden="true"
            >
              <defs>
                <linearGradient id="incomeFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#2563EB" stopOpacity="0.25" />
                  <stop offset="100%" stopColor="#2563EB" stopOpacity="0.02" />
                </linearGradient>
                <linearGradient id="expenseFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#34D399" stopOpacity="0.3" />
                  <stop offset="100%" stopColor="#34D399" stopOpacity="0.02" />
                </linearGradient>
              </defs>
              <path
                d="M0,80 C30,70 50,45 80,50 C110,55 130,30 160,35 C190,40 210,20 240,22 C265,24 285,12 300,10 L300,110 L0,110 Z"
                fill="url(#incomeFill)"
              />
              <path
                d="M0,80 C30,70 50,45 80,50 C110,55 130,30 160,35 C190,40 210,20 240,22 C265,24 285,12 300,10"
                fill="none"
                stroke="#2563EB"
                strokeWidth="2"
              />
              <path
                d="M0,95 C35,92 55,80 85,84 C115,88 140,68 170,72 C200,76 225,58 250,60 C272,62 288,50 300,48 L300,110 L0,110 Z"
                fill="url(#expenseFill)"
              />
              <path
                d="M0,95 C35,92 55,80 85,84 C115,88 140,68 170,72 C200,76 225,58 250,60 C272,62 288,50 300,48"
                fill="none"
                stroke="#34D399"
                strokeWidth="2"
              />
            </svg>
          </div>
        </div>
      </div>
    </div>
  );
}

/* Mocked journal-entry / ledger UI card for the double-entry deep dive. */
function LedgerCard() {
  const rows = [
    {
      account: "Accounts Receivable",
      ref: "1200",
      debit: "$4,800.00",
      credit: "—",
    },
    { account: "Sales Revenue", ref: "4000", debit: "—", credit: "$4,500.00" },
    {
      account: "Sales Tax Payable",
      ref: "2200",
      debit: "—",
      credit: "$300.00",
    },
  ];
  return (
    <div className="card overflow-hidden shadow-lifted transition-transform duration-300 hover:-translate-y-1">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-line bg-surface/70 px-5 py-3.5">
        <div>
          <p className="text-xs font-bold text-navy">Journal Entry #JE-1042</p>
          <p className="text-[10px] text-slate-500">
            Invoice INV-0187 · Jul 29, 2026
          </p>
        </div>
        <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-3 py-1 text-[11px] font-semibold text-emerald-600 ring-1 ring-emerald-200">
          <FiCheckCircle size={12} /> Balanced
        </span>
      </div>
      <div className="px-5 py-4">
        <div className="grid grid-cols-[1fr_auto_auto] gap-x-6 text-[11px]">
          <p className="pb-2 font-semibold uppercase tracking-wider text-slate-400">
            Account
          </p>
          <p className="pb-2 text-right font-semibold uppercase tracking-wider text-slate-400">
            Debit
          </p>
          <p className="pb-2 text-right font-semibold uppercase tracking-wider text-slate-400">
            Credit
          </p>
          {rows.map((row) => (
            <React.Fragment key={row.account}>
              <p className="border-t border-line py-2.5 font-medium text-navy">
                {row.account}
                <span className="ml-2 text-[10px] font-normal text-slate-400">
                  {row.ref}
                </span>
              </p>
              <p className="border-t border-line py-2.5 text-right tabular-nums text-slate-600">
                {row.debit}
              </p>
              <p className="border-t border-line py-2.5 text-right tabular-nums text-slate-600">
                {row.credit}
              </p>
            </React.Fragment>
          ))}
          <p className="border-t-2 border-navy/20 py-2.5 text-xs font-bold text-navy">
            Totals
          </p>
          <p className="border-t-2 border-navy/20 py-2.5 text-right text-xs font-bold tabular-nums text-navy">
            $4,800.00
          </p>
          <p className="border-t-2 border-navy/20 py-2.5 text-right text-xs font-bold tabular-nums text-navy">
            $4,800.00
          </p>
        </div>
        <p className="mt-3 rounded-lg bg-primary/5 px-3 py-2 text-[11px] text-primary">
          Debits equal credits on every entry — your ledger can never drift out
          of balance.
        </p>
      </div>
    </div>
  );
}

/* Mocked P&L / bar-chart UI card for the reports deep dive. */
function ReportsCard() {
  const bars = [
    { month: "Apr", income: 64, expense: 42 },
    { month: "May", income: 78, expense: 48 },
    { month: "Jun", income: 92, expense: 55 },
  ];
  return (
    <div className="card overflow-hidden shadow-lifted transition-transform duration-300 hover:-translate-y-1">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-line bg-surface/70 px-5 py-3.5">
        <div>
          <p className="text-xs font-bold text-navy">Profit &amp; Loss</p>
          <p className="text-[10px] text-slate-500">Q2 2026 · Accrual basis</p>
        </div>
        <div className="flex items-center gap-1.5">
          {["PDF", "CSV", "Excel"].map((fmt) => (
            <span
              key={fmt}
              className="inline-flex items-center gap-1 rounded-full border border-line bg-white px-2.5 py-1 text-[10px] font-semibold text-slate-500"
            >
              <FiDownload size={10} /> {fmt}
            </span>
          ))}
        </div>
      </div>
      <div className="px-5 py-4">
        <div className="flex items-end justify-around gap-4 rounded-lg border border-line bg-surface/40 px-4 pb-2 pt-4">
          {bars.map(({ month, income, expense }) => (
            <div key={month} className="flex flex-col items-center gap-1.5">
              <div className="flex items-end gap-1.5">
                <div
                  className="w-5 rounded-t bg-gradient-to-t from-primary to-blue-400"
                  style={{ height: `${income}px` }}
                />
                <div
                  className="w-5 rounded-t bg-slate-200"
                  style={{ height: `${expense}px` }}
                />
              </div>
              <p className="text-[10px] font-medium text-slate-500">{month}</p>
            </div>
          ))}
        </div>
        <div className="mt-4 space-y-2 text-xs">
          <div className="flex items-center justify-between">
            <p className="text-slate-500">Total Revenue</p>
            <p className="font-semibold tabular-nums text-navy">$348,200</p>
          </div>
          <div className="flex items-center justify-between">
            <p className="text-slate-500">Total Expenses</p>
            <p className="font-semibold tabular-nums text-navy">$214,600</p>
          </div>
          <div className="flex items-center justify-between border-t border-line pt-2">
            <p className="font-bold text-navy">Net Profit</p>
            <p className="inline-flex items-center gap-1.5 font-bold tabular-nums text-navy">
              $133,600
              <span className="inline-flex items-center gap-0.5 rounded-full bg-emerald-50 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-600">
                <FiTrendingUp size={9} /> +18.2%
              </span>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Home() {
  const [openFaq, setOpenFaq] = useState(0);
  const teaserPlans = FALLBACK_PLANS;

  return (
    <>
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div
          className="pointer-events-none absolute -top-32 right-[-10%] h-[28rem] w-[28rem] rounded-full bg-primary/10 blur-3xl"
          aria-hidden="true"
        />
        <div
          className="pointer-events-none absolute -bottom-24 left-[-8%] h-80 w-80 rounded-full bg-blue-100/60 blur-3xl"
          aria-hidden="true"
        />
        <div className="section-container relative grid grid-cols-1 items-center gap-12 py-16 lg:grid-cols-2 lg:py-24">
          <div>
            <p className="inline-flex items-center gap-2 rounded-full border border-line bg-white/70 px-3.5 py-1.5 text-xs font-semibold text-primary shadow-sm backdrop-blur">
              <span className="h-1.5 w-1.5 rounded-full bg-primary" />
              Trusted accounting for modern businesses
            </p>
            <h1 className="mt-5 text-4xl font-extrabold leading-tight tracking-tight sm:text-5xl">
              <span className="text-primary">S</span>mart Accounting Software
              for <span className="text-primary">Your Business Growth</span>
            </h1>
            <p className="mt-5 max-w-lg text-base text-slate-600">
              Manage your finances, track expenses, send invoices, reconcile
              bank accounts, and generate powerful reports — all in one
              easy-to-use platform.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link to="/register" className="btn-primary">
                Get Started
              </Link>
              <Link to="/pricing" className="btn-outline">
                View Pricing
              </Link>
            </div>
            <div className="mt-7 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-slate-600">
              {HERO_CHECKS.map((item) => (
                <span key={item} className="inline-flex items-center gap-1.5">
                  <FiCheckCircle className="text-emerald-500" size={15} />
                  {item}
                </span>
              ))}
            </div>
          </div>

          <DashboardPreview />
        </div>
      </section>

      {/* Feature grid */}
      <section className="section-container py-20 lg:py-24">
        <SectionHeading
          overline="Everything in one place"
          title="Everything You Need to Run Your Business"
          subtitle="Powerful features designed to save you time and help you make better financial decisions."
        />
        <div className="mt-14 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {FEATURES.map(({ icon: Icon, title, copy }, i) => (
            <Reveal key={title} delay={(i % 3) * 90}>
              <div className="card h-full p-6 ring-1 ring-transparent transition-all duration-300 hover:-translate-y-1 hover:shadow-lifted hover:ring-primary/20">
                <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary/10 text-primary">
                  <Icon size={20} />
                </span>
                <h3 className="mt-4 text-lg font-semibold">{title}</h3>
                <p className="mt-2 text-sm text-slate-600">{copy}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section className="border-y border-line bg-surface">
        <div className="section-container py-20 lg:py-24">
          <SectionHeading
            overline="How it works"
            title="Up and Running in Three Simple Steps"
            subtitle="From sign-up to spotless books — most teams are fully onboarded in less than a day."
          />
          <div className="relative mt-16">
            <div
              className="pointer-events-none absolute left-0 right-0 top-7 hidden border-t-2 border-dashed border-primary/25 lg:block"
              aria-hidden="true"
            />
            <div className="grid grid-cols-1 gap-10 lg:grid-cols-3 lg:gap-8">
              {STEPS.map(({ icon: Icon, title, copy }, i) => (
                <Reveal key={title} delay={i * 120}>
                  <div className="relative text-center lg:px-4">
                    <div className="relative mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-primary to-blue-500 text-white shadow-lifted">
                      <Icon size={22} />
                      <span className="absolute -right-2 -top-2 flex h-6 w-6 items-center justify-center rounded-full bg-navy text-[11px] font-bold text-white ring-2 ring-surface">
                        {i + 1}
                      </span>
                    </div>
                    <h3 className="mt-6 text-lg font-semibold">{title}</h3>
                    <p className="mx-auto mt-2 max-w-xs text-sm leading-relaxed text-slate-500">
                      {copy}
                    </p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Deep dive: double-entry accounting */}
      <section className="section-container grid grid-cols-1 items-center gap-12 py-20 lg:grid-cols-2 lg:gap-16 lg:py-24">
        <Reveal>
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.22em] text-primary">
              Built on fundamentals
            </p>
            <h2 className="mt-3 text-3xl font-bold tracking-tight sm:text-4xl">
              Real Double-Entry Accounting
            </h2>
            <p className="mt-4 text-base leading-relaxed text-slate-500">
              Money Flow Accounting isn't a spreadsheet with a nicer face. Every
              invoice, bill, and payment posts a proper journal entry behind the
              scenes, so your ledger stays balanced — always.
            </p>
            <ul className="mt-6 space-y-3">
              {[
                "Automatic journal entries for every transaction",
                "Full chart of accounts you control",
                "Accrual or cash basis — switch anytime",
                "Accountant-ready books at year end",
              ].map((item) => (
                <li
                  key={item}
                  className="flex items-start gap-3 text-sm text-slate-600"
                >
                  <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                    <FiCheck size={12} />
                  </span>
                  {item}
                </li>
              ))}
            </ul>
            <Link
              to="/features"
              className="mt-7 inline-flex items-center gap-2 text-sm font-semibold text-primary hover:text-blue-700"
            >
              Explore all features <FiArrowRight size={15} />
            </Link>
          </div>
        </Reveal>
        <Reveal delay={120}>
          <LedgerCard />
        </Reveal>
      </section>

      {/* Deep dive: reports */}
      <section className="bg-surface">
        <div className="section-container grid grid-cols-1 items-center gap-12 py-20 lg:grid-cols-2 lg:gap-16 lg:py-24">
          <Reveal delay={120} className="order-last lg:order-first">
            <ReportsCard />
          </Reveal>
          <Reveal>
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.22em] text-primary">
                Insight on autopilot
              </p>
              <h2 className="mt-3 text-3xl font-bold tracking-tight sm:text-4xl">
                Reports That Run Themselves
              </h2>
              <p className="mt-4 text-base leading-relaxed text-slate-500">
                P&amp;L, balance sheet, cash flow, aging, and tax summaries —
                generated in real time from your live ledger. No exports, no
                formulas, no waiting on month-end.
              </p>
              <ul className="mt-6 space-y-3">
                {[
                  "Real-time P&L, balance sheet, and cash flow",
                  "Compare periods and drill into any number",
                  "One-click export to PDF, CSV, or Excel",
                  "Scheduled reports delivered to your inbox",
                ].map((item) => (
                  <li
                    key={item}
                    className="flex items-start gap-3 text-sm text-slate-600"
                  >
                    <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                      <FiCheck size={12} />
                    </span>
                    {item}
                  </li>
                ))}
              </ul>
              <Link
                to="/features"
                className="mt-7 inline-flex items-center gap-2 text-sm font-semibold text-primary hover:text-blue-700"
              >
                See reporting in detail <FiArrowRight size={15} />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Security & trust band */}
      <section className="relative overflow-hidden bg-navy">
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.35]"
          aria-hidden="true"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.045) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.045) 1px, transparent 1px)",
            backgroundSize: "44px 44px",
          }}
        />
        <div
          className="pointer-events-none absolute -top-40 left-1/2 h-96 w-[42rem] -translate-x-1/2 rounded-full bg-primary/20 blur-3xl"
          aria-hidden="true"
        />
        <div className="section-container relative py-20 lg:py-24">
          <div className="grid grid-cols-1 items-start gap-12 lg:grid-cols-2 lg:gap-16">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.22em] text-blue-300">
                Security &amp; trust
              </p>
              <h2 className="mt-3 text-3xl font-bold tracking-tight text-white sm:text-4xl">
                Your Books, Protected Like a Vault
              </h2>
              <p className="mt-4 max-w-lg text-base leading-relaxed text-slate-300">
                Financial data deserves financial-grade protection. Money Flow
                Accounting is engineered with defense in depth — from the login
                screen to the last byte at rest.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                {SECURITY_BADGES.map((badge) => (
                  <span
                    key={badge}
                    className="inline-flex items-center gap-2 rounded-lg border border-white/15 bg-white/5 px-4 py-2 text-xs font-semibold text-blue-100 backdrop-blur"
                  >
                    <FiShield size={13} className="text-blue-300" />
                    {badge}
                  </span>
                ))}
              </div>
              <div className="mt-10 hidden items-center gap-4 rounded-2xl border border-white/10 bg-white/5 p-5 backdrop-blur lg:flex">
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-primary/20 text-blue-300">
                  <FiKey size={20} />
                </span>
                <p className="text-sm leading-relaxed text-slate-300">
                  Only you decide who sees what. Owner, accountant, or read-only
                  auditor — every role is scoped, logged, and revocable in one
                  click.
                </p>
              </div>
            </div>
            <ul className="space-y-4">
              {SECURITY_POINTS.map(({ icon: Icon, title, copy }, i) => (
                <Reveal key={title} delay={i * 80}>
                  <li className="flex items-start gap-4 rounded-2xl border border-white/10 bg-white/5 p-5 backdrop-blur transition-colors duration-300 hover:border-white/20 hover:bg-white/[0.08]">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/20 text-blue-300">
                      <Icon size={17} />
                    </span>
                    <span>
                      <span className="block text-sm font-semibold text-white">
                        {title}
                      </span>
                      <span className="mt-1 block text-sm text-slate-300/90">
                        {copy}
                      </span>
                    </span>
                  </li>
                </Reveal>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* What the software sets out to do */}
      <section className="section-container py-20 lg:py-24">
        <SectionHeading
          overline="Why Money Flow Accounting"
          title="Don't Take Our Word for It"
          subtitle="Try it for yourself."
        />
        <div className="mt-14 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {PROMISES.map(({ icon: Icon, title, body }, i) => (
            <Reveal key={title} delay={i * 100}>
              <div className="card flex h-full flex-col p-7 transition-all duration-300 hover:-translate-y-1 hover:shadow-lifted">
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary">
                  <Icon size={20} />
                </span>
                <p className="mt-5 text-base font-bold text-navy">{title}</p>
                <p className="mt-2 flex-1 text-sm leading-relaxed text-slate-600">
                  {body}
                </p>
              </div>
            </Reveal>
          ))}
        </div>
        <div className="mt-12 text-center">
          <Link
            to="/register"
            className="btn-primary inline-flex items-center gap-2"
          >
            Get started <FiArrowRight size={16} />
          </Link>
        </div>
      </section>

      {/* Pricing teaser */}
      <section className="border-y border-line bg-surface">
        <div className="section-container py-20 lg:py-24">
          <SectionHeading
            overline="Simple, transparent pricing"
            title="A Plan for Every Stage of Growth"
            subtitle="One payment a year. Upgrade, downgrade or cancel whenever you need to."
          />
          <div className="mx-auto mt-14 grid max-w-5xl grid-cols-1 gap-6 md:grid-cols-3">
            {teaserPlans.map((plan, i) => (
              <Reveal key={plan.id} delay={i * 100}>
                <div
                  className={`relative flex h-full flex-col rounded-2xl bg-white p-7 transition-all duration-300 hover:-translate-y-1 ${
                    plan.popular
                      ? "shadow-lifted ring-2 ring-primary"
                      : "shadow-card ring-1 ring-line hover:shadow-lifted"
                  }`}
                >
                  {plan.popular && (
                    <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-primary px-3.5 py-1 text-[11px] font-bold uppercase tracking-wider text-white shadow-card">
                      Most Popular
                    </span>
                  )}
                  <h3 className="text-base font-semibold">{plan.name}</h3>
                  <p className="mt-3">
                    <span className="text-4xl font-extrabold tracking-tight text-navy">
                      {formatCents(plan.priceCents)}
                    </span>
                    <span className="text-sm text-slate-500"> / year</span>
                  </p>
                  <p className="mt-2 text-xs leading-relaxed text-slate-500">
                    {plan.description}
                  </p>
                  <ul className="mt-5 flex-1 space-y-2.5">
                    {plan.features.slice(0, 4).map((feature) => (
                      <li
                        key={feature}
                        className="flex items-center gap-2.5 text-sm text-slate-600"
                      >
                        <FiCheckCircle
                          className="shrink-0 text-emerald-500"
                          size={14}
                        />
                        {feature}
                      </li>
                    ))}
                  </ul>
                  <Link
                    to="/pricing"
                    className={`mt-6 ${plan.popular ? "btn-primary" : "btn-outline"} w-full`}
                  >
                    Get Started
                  </Link>
                </div>
              </Reveal>
            ))}
          </div>
          <p className="mt-10 text-center text-sm text-slate-500">
            Need the full feature comparison?{" "}
            <Link
              to="/pricing"
              className="inline-flex items-center gap-1.5 font-semibold text-primary hover:text-blue-700"
            >
              See full pricing <FiArrowRight size={14} />
            </Link>
          </p>
        </div>
      </section>

      {/* What customers say — nothing is shown until real reviews exist */}
      <HomeReviews />

      {/* FAQ teaser */}
      <section className="section-container py-20 lg:py-24">
        <SectionHeading
          overline="Good to know"
          title="Frequently Asked Questions"
          subtitle="Quick answers to the questions we hear most. Can't find yours? We're one message away."
        />
        <div className="mx-auto mt-12 max-w-3xl space-y-3">
          {FAQS.map((faq, i) => {
            const open = openFaq === i;
            return (
              <div key={faq.q} className="card overflow-hidden">
                <button
                  type="button"
                  onClick={() => setOpenFaq(open ? null : i)}
                  className="flex w-full items-center justify-between px-5 py-4 text-left"
                  aria-expanded={open}
                >
                  <span className="text-sm font-semibold text-navy">
                    {faq.q}
                  </span>
                  <FiChevronDown
                    className={`shrink-0 text-slate-400 transition-transform ${
                      open ? "rotate-180" : ""
                    }`}
                    size={18}
                  />
                </button>
                {open && (
                  <p className="border-t border-line px-5 py-4 text-sm leading-relaxed text-slate-600">
                    {faq.a}
                  </p>
                )}
              </div>
            );
          })}
        </div>
        <p className="mt-8 text-center text-sm text-slate-500">
          Still have questions?{" "}
          <Link
            to="/contact"
            className="inline-flex items-center gap-1.5 font-semibold text-primary hover:text-blue-700"
          >
            Talk to our team <FiArrowRight size={14} />
          </Link>
        </p>
      </section>

      <CtaBanner />
    </>
  );
}
